import moment from 'moment';
import { toast } from 'react-toastify';
import { getSubmissionBackendUrl } from '../constants/api-urls';
import {
  acceptAllocation,
  updateCampaignWithSongInfo,
} from '../services/campaignSubmissionService';
import { buildShareUrl, createShareLinkToken } from '../services/shareLinkService';
import { getCurrentUserProfileObjectId } from '../utils';

export interface CampaignSongPlacement {
  campaignId?: string;
  artistName?: string;
  trackName?: string;
  songPosition: number | string;
  playlistId: string;
  playlistName: string;
  playlistURL?: string;
  songURI: string;
  songShareLink?: string;
  addedByCuratorId?: string;
  curatorName?: string;
  status?: string;
  startTime?: string | null;
  endTime?: string | null;
  notes?: string;
  addedBy: string;
  isScheduled?: boolean;
  scheduledStartTime?: string | null;
  playlistEntryId?: string;
  curatorProfileObjectId?: string;
}

interface NotifyCampaignPlacementsParams {
  placements: CampaignSongPlacement[];
  campaignData?: {
    _id?: string;
    allocationMode?: string;
    curatorAllocations?: Array<{
      _id?: string;
      winnerId?: string;
      curatorIds?: string[];
    }>;
  } | null;
  refreshToken: string;
  usesAccountCampaignPlaylists: boolean;
}

export const notifyCampaignSongPlacements = async ({
  placements,
  campaignData,
  refreshToken,
  usesAccountCampaignPlaylists,
}: NotifyCampaignPlacementsParams): Promise<void> => {
  if (!placements.length) {
    return;
  }

  if (placements.some(placement => !placement.songURI)) {
    console.error('Song URI missing in placement, cannot notify backend', placements);
    toast.error('Could not process campaign song - missing URI');
    return;
  }

  const fallbackProfileId = usesAccountCampaignPlaylists
    ? null
    : await getCurrentUserProfileObjectId();

  const placementsByProfile = new Map<string, CampaignSongPlacement[]>();
  for (const placement of placements) {
    const profileId = placement.curatorProfileObjectId || fallbackProfileId;
    if (!profileId) {
      toast.error(
        'Unable to identify curator profile for a selected playlist. Please refresh and try again.'
      );
      return;
    }
    const existing = placementsByProfile.get(profileId) || [];
    existing.push(placement);
    placementsByProfile.set(profileId, existing);
  }

  if (!getSubmissionBackendUrl()) {
    console.error('Submission backend URL not defined in environment variables');
    toast.error('Backend configuration error - cannot update campaign status');
    return;
  }

  const isSplitCampaign = campaignData?.allocationMode === 'split';

  for (const [curatorProfileId, profilePlacements] of Array.from(placementsByProfile.entries())) {
    const placementsWithShareLinks = await Promise.all(
      profilePlacements.map(async placement => {
        const token = await createShareLinkToken({
          uri: placement.songURI,
          playlistId: placement.playlistId,
          id: placement.songURI.split('spotify:track:')[1],
          ref: refreshToken,
          position: placement.songPosition,
          originalDate: placement.startTime ? new Date(placement.startTime) : new Date(),
          endTime: placement.endTime ? moment(placement.endTime).format('DD-MM-YY, hh:mm A') : '',
        });

        return {
          ...placement,
          songShareLink: buildShareUrl(token),
        };
      })
    );

    const firstPlacement = placementsWithShareLinks[0];
    const hasScheduledPlacement = placementsWithShareLinks.some(item => item.isScheduled);
    const earliestScheduledStart = placementsWithShareLinks
      .filter(item => item.isScheduled && item.scheduledStartTime)
      .map(item => new Date(item.scheduledStartTime as string))
      .sort((a, b) => a.getTime() - b.getTime())[0];
    const normalizedPlacements = placementsWithShareLinks.map(placement => ({
      playlistId: placement.playlistId,
      playlistName: placement.playlistName,
      playlistURL: placement.playlistURL,
      songPosition: placement.songPosition,
      songURI: placement.songURI,
      songShareLink: placement.songShareLink,
      startTime: placement.startTime,
      endTime: placement.endTime,
      notes: placement.notes,
      isScheduled: placement.isScheduled,
      scheduledStartTime: placement.scheduledStartTime,
      playlistEntryId: placement.playlistEntryId,
    }));

    if (isSplitCampaign) {
      const curatorAllocation = campaignData?.curatorAllocations?.find(allocation => {
        const winnerMatch = allocation?.winnerId?.toString?.() === curatorProfileId;
        const candidateMatch = Array.isArray(allocation?.curatorIds)
          ? allocation.curatorIds.some(id => id?.toString?.() === curatorProfileId)
          : false;
        return winnerMatch || candidateMatch;
      });

      await acceptAllocation({
        campaignId: firstPlacement.campaignId,
        curatorId: curatorProfileId,
        allocationId: curatorAllocation?._id,
        playlistId: firstPlacement.playlistId,
        playlistName: firstPlacement.playlistName,
        playlistURL: firstPlacement.playlistURL,
        songPosition: firstPlacement.songPosition,
        songURI: firstPlacement.songURI,
        songShareLink: firstPlacement.songShareLink,
        startTime: firstPlacement.startTime,
        endTime: firstPlacement.endTime,
        notes: firstPlacement.notes,
        isScheduled: hasScheduledPlacement,
        scheduledStartTime: earliestScheduledStart?.toISOString() || null,
        playlistEntryId: firstPlacement.playlistEntryId,
        placements: normalizedPlacements,
      });
    } else {
      await updateCampaignWithSongInfo({
        ...firstPlacement,
        addedBy: curatorProfileId,
        isScheduled: hasScheduledPlacement,
        scheduledStartTime: earliestScheduledStart?.toISOString() || null,
        playlistEntryId: firstPlacement.playlistEntryId,
        placements: normalizedPlacements,
      });
    }
  }
};
